import { useEffect } from 'react';
import { Link } from 'wouter';
import { usePageMeta } from '../../lib/usePageMeta';
import { REPORT_PRICE } from '../../lib/pricing';
import { routes } from '../../lib/assessment/routes';
import { buttonVariants } from '../ui/button';
import { cn } from '../../lib/utils';
import { ArrowRight } from './icons';

/**
 * The public homepage, migrated from the legacy application's
 * `src/pages/landing.tsx` (V2-018C-2).
 *
 * Parity migration, not a redesign. Copy, headings, section order, CTAs, FAQ
 * entries, anchors, and structured data are reproduced exactly. Four categories
 * of change are present, and only those four:
 *
 * 1. **V2 routing.** Legacy `/assessment`, `/work-fit/assessment`, and
 *    `/perspective/assessment` become this repository's single runner route
 *    (`/assessment/:type`). Every other destination is unchanged.
 * 2. **The shell.** Legacy inlines a header and footer on every page; here the
 *    Experience Shell supplies both, so this file is the page's main content
 *    only. The `#assessments` and `#faq` anchors are preserved.
 * 3. **Obsolete technology.** Clerk (`useUser`, `useClerk`) and the react-query
 *    `PrefetchLink` have no V2 equivalent. Header account state is the shell's
 *    concern; prefetching is dropped.
 * 4. **Accessibility.** Legacy nests `<Button>` and `<button>` inside `<Link>`,
 *    which produces an interactive control inside an anchor. Here the anchor
 *    itself carries the button styling through `buttonVariants`, so the
 *    rendering is unchanged and the markup is valid.
 */
const FAQ_ITEMS = [
  {
    q: 'What is High Performer OS?',
    a: 'High Performer OS is a performance assessment platform for capable professionals who want to understand what is quietly affecting their execution. It provides free diagnostic assessments and optional full reports covering bottleneck patterns, work environment fit, and future perspective tools.',
  },
  {
    q: 'Who is High Performer OS for?',
    a: 'High Performer OS is designed for professionals, founders, operators, and leaders who are already capable but feel a persistent gap between their effort and their output.',
  },
  {
    q: 'What does the Bottleneck Assessment measure?',
    a: 'The Bottleneck Assessment measures eight internal performance categories: avoidance, overextension, identity pressure, focus, recovery, emotional regulation, goal clarity, and isolation. You receive your High Performer Type, four composite scores, and your top bottleneck categories.',
  },
  {
    q: 'What does the Work Fit Assessment measure?',
    a: 'The Work Fit Assessment measures ten dimensions of work environment fit: autonomy, structure preference, pace, decision-making authority, feedback style, culture climate, energy sustainability, values alignment, political tolerance, and role clarity. It identifies the environments and management styles most compatible with how you operate.',
  },
  {
    q: 'What does the Perspective Assessment measure?',
    a: 'The Perspective Assessment identifies the dominant interpretive pattern shaping how you process situations, make decisions, and respond to pressure. It measures eight patterns: Future Controller, Past Replayer, Approval Seeker, Comfort Protector, Outcome Identifier, Negative Forecaster, Self-Debater, and Present Operator. You receive your dominant pattern, intensity level, core lens, strength, cost, immediate reframe, and a full pattern score profile.',
  },
  {
    q: 'Is the assessment free?',
    a: 'Yes. The Bottleneck Assessment, the Work Fit Assessment, and the Perspective Assessment are all free. You receive your type, dimension scores, and top factors at no cost. A paid full report is available as an optional upgrade after you see your results.',
  },
  {
    q: 'What is included in the full reports?',
    a: 'The Bottleneck Full Report expands on your High Performer Type, bottleneck patterns, score interpretation, personalized stop and start behaviors, and reset tools. The Work Fit Full Report expands on how your role, pace, autonomy, decision space, feedback environment, energy demands, values fit, and role clarity align with how you perform best. The Perspective Full Report expands on your dominant pattern, self-talk profile, interpretation traps, time orientation, belief friction, and ABCDE reframe exercises, and includes a 7-Day Reset Workbook and a 30-Day Practice Workbook. All reports are delivered as downloadable PDFs.',
  },
  {
    q: 'What does a full report cost?',
    a: `Each full report is $${REPORT_PRICE} as a one-time payment.`,
  },
  {
    q: 'Is High Performer OS medical or clinical advice?',
    a: 'No. High Performer OS is a personal development and coaching tool only. It is not medical, psychological, or clinical advice and is not a diagnostic instrument.',
  },
  {
    q: 'How long does the assessment take?',
    a: 'Each assessment takes about 7 minutes. You answer 50 statements using a rating scale of 1 to 5.',
  },
];

// Ring classes are mandatory on every link (visual system section E.7) and are
// written out at each call site rather than hoisted into a constant: the
// contract test reads the `className` source text, so a shared constant would
// satisfy the runtime and fail the guard.

export function Landing() {
  usePageMeta({
    title: 'High Performer OS | Assessments for High Performers',
    description:
      'Three free assessments for high performers. Identify your bottleneck pattern, work fit, and interpretive lens. Free results. Optional paid full reports.',
    canonical: 'https://highperformeros.com/',
    ogImage: 'https://highperformeros.com/logo-white-v2.webp',
  });

  // Structured data, migrated unchanged. `application/ld+json` is a data block
  // rather than executable script, so the `script-src 'self'` policy does not
  // apply to it; confirm in the browser check that no CSP violation is logged.
  useEffect(() => {
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.id = 'faq-schema';
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(faqScript);

    const siteScript = document.createElement('script');
    siteScript.type = 'application/ld+json';
    siteScript.id = 'website-schema';
    siteScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'High Performer OS',
      url: 'https://highperformeros.com',
      description:
        'Performance assessments for high performers. Identify internal friction, evaluate work environment fit, and understand your interpretive patterns.',
    });
    document.head.appendChild(siteScript);

    const orgScript = document.createElement('script');
    orgScript.type = 'application/ld+json';
    orgScript.id = 'org-schema';
    orgScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'High Performer OS',
      url: 'https://highperformeros.com',
      description:
        'Three free performance assessments for high performers covering internal friction patterns, work environment fit, and interpretive perspective patterns.',
      sameAs: [],
    });
    document.head.appendChild(orgScript);

    return () => {
      const faqEl = document.getElementById('faq-schema');
      if (faqEl) document.head.removeChild(faqEl);
      const siteEl = document.getElementById('website-schema');
      if (siteEl) document.head.removeChild(siteEl);
      const orgEl = document.getElementById('org-schema');
      if (orgEl) document.head.removeChild(orgEl);
    };
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="flex flex-col items-center justify-center px-4 py-16 text-center max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-4 py-2 rounded-full mb-8">
          Performance Diagnostics for High Performers
        </div>
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight">
          You are capable. So why does{' '}
          <span className="text-primary">everything still feel heavier than it should?</span>
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
          High Performer OS helps you identify whether the friction is coming from an internal
          bottleneck, a work environment mismatch, or the way you interpret pressure, uncertainty,
          discomfort, and outcomes.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href={routes.assessment('bottleneck')}
            data-testid="hero-cta-bottleneck"
            className={cn(
              buttonVariants({ size: 'lg' }),
              'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(245,184,0,0.25)] gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Start with the Bottleneck Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
          <a
            href="#assessments"
            className={cn(
              buttonVariants({ size: 'lg', variant: 'outline' }),
              'text-base px-8 py-6 h-auto gap-2',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Explore the Assessments
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-4">
          Start with Bottleneck, or explore all three assessments to choose the one that fits what
          you are facing.
        </p>
      </section>

      {/* Assessment Cards */}
      <section id="assessments" className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
              The Assessments
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">
              Three diagnostics. Each one answers a different question.
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Most high performers know something is creating friction. The harder part is knowing
              whether the issue is internal, environmental, or a pattern in how they are
              interpreting what is happening. Each assessment addresses one of those sources.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {/* Bottleneck */}
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-7 space-y-4 flex flex-col">
              <div>
                <p className="text-xs font-medium text-foreground/60 mb-2 italic">
                  What internal pattern is creating drag?
                </p>
                <h3 className="text-lg font-bold">Bottleneck Assessment</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                Identifies internal performance friction patterns affecting clarity, consistency,
                confidence, execution, recovery, and follow-through.
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <Link
                  href={routes.assessment('bottleneck')}
                  className={cn(
                    buttonVariants(),
                    'w-full bg-primary text-primary-foreground gap-2',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Take the Bottleneck Assessment
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href={routes.bottleneck}
                  className={cn(
                    'w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Work Fit */}
            <div className="rounded-2xl border border-border bg-card p-7 space-y-4 flex flex-col">
              <div>
                <p className="text-xs font-medium text-foreground/60 mb-2 italic">
                  Is your environment fighting how you perform best?
                </p>
                <h3 className="text-lg font-bold">Work Fit Assessment</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                Evaluates whether your current work environment supports how you operate best,
                across autonomy, pace, culture, feedback, and role clarity.
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <Link
                  href={routes.assessment('work_fit')}
                  className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'w-full gap-2',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Take the Work Fit Assessment
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href={routes.workFit}
                  className={cn(
                    'w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Learn More
                </Link>
              </div>
            </div>

            {/* Perspective */}
            <div className="rounded-2xl border border-border bg-card p-7 space-y-4 flex flex-col">
              <div>
                <p className="text-xs font-medium text-foreground/60 mb-2 italic">
                  How are you interpreting pressure, uncertainty, and outcomes?
                </p>
                <h3 className="text-lg font-bold">Perspective Assessment</h3>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                Identifies the lens through which you interpret pressure, uncertainty, discomfort,
                setbacks, success, failure, expectations, focus, control, and yourself.
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <Link
                  href={routes.assessment('perspective')}
                  className={cn(
                    buttonVariants({ variant: 'outline' }),
                    'w-full gap-2',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Take the Perspective Assessment
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href={routes.perspective}
                  className={cn(
                    'w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                  )}
                >
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why HP OS Exists */}
      <section className="bg-card border-y border-border py-20 px-4">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Why This Exists
          </p>
          <h2 className="text-2xl md:text-3xl font-bold">
            A lot of high performers blame themselves when things start feeling heavier than they
            should.
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed text-left max-w-2xl mx-auto">
            <p>They assume they need more discipline, more motivation, better habits, or a harder push.</p>
            <p>But sometimes the problem is not effort.</p>
            <p>
              Sometimes the issue is an internal bottleneck. Sometimes the role or environment does
              not fit how they operate best. Sometimes their perspective is turning pressure,
              uncertainty, or outcomes into more weight than the situation actually requires.
            </p>
            <p>
              High Performer OS was built to help separate those three sources of friction. It does
              not diagnose or prescribe. It gives the friction a name so the right work can begin.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">How it works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Choose your assessment',
                desc: 'Start with the Bottleneck Assessment to identify internal performance patterns, the Work Fit Assessment to map your environment needs, or the Perspective Assessment to identify the interpretive lens shaping your decisions. All three are free.',
              },
              {
                step: '02',
                title: 'Get your free results',
                desc: 'Complete the assessment in about 7 minutes and immediately see your profile, top dimensions, and the patterns most relevant to your performance.',
              },
              {
                step: '03',
                title: 'Use what you learn',
                desc: 'Apply your results to current work decisions, role evaluations, or the sustained work of identifying and changing the patterns that are creating the most friction.',
              },
            ].map((s) => (
              <div key={s.step} className="text-center space-y-3">
                <div className="text-3xl font-bold text-primary/30">{s.step}</div>
                <h4 className="font-bold">{s.title}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-card border-t border-border py-20 px-4 text-center">
        <div className="max-w-2xl mx-auto space-y-6">
          <h2 className="text-2xl md:text-3xl font-bold">See where the friction is coming from.</h2>
          <p className="text-muted-foreground">
            All three assessments are free. Each one is a diagnostic starting point for
            understanding what is creating drag in how you operate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            <Link
              href={routes.bottleneck}
              className={cn(
                buttonVariants({ size: 'lg' }),
                'text-base px-8 py-6 h-auto bg-primary text-primary-foreground hover:bg-primary/90 gap-2',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              Bottleneck Assessment
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href={routes.workFit}
              className={cn(
                buttonVariants({ size: 'lg', variant: 'outline' }),
                'text-base px-8 py-6 h-auto gap-2',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              Work Fit Assessment
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              href={routes.perspective}
              className={cn(
                buttonVariants({ size: 'lg', variant: 'outline' }),
                'text-base px-8 py-6 h-auto gap-2',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              )}
            >
              Perspective Assessment
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About High Performer OS */}
      <section className="py-16 px-4 border-t border-border">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">
            About High Performer OS
          </p>
          <h2 className="text-xl md:text-2xl font-bold mb-4">Why this was built</h2>
          <p className="text-muted-foreground text-sm leading-relaxed max-w-xl mx-auto mb-6">
            High performers were recognizing themselves in the patterns. The next question was
            always the same: "What do I do now?" High Performer OS was built to answer that
            question.
          </p>
          <Link
            href={routes.why}
            className={cn(
              'inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors',
              'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            )}
          >
            Read why I built this
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">
            Frequently asked questions
          </h2>
          <div className="divide-y divide-border" data-testid="faq-list">
            {FAQ_ITEMS.map(({ q, a }) => (
              <div key={q} className="py-6">
                <h3 className="font-semibold text-base mb-2">{q}</h3>
                <p className="text-muted-foreground leading-relaxed text-sm">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export { FAQ_ITEMS };
